/**
 * Invite & Earn — client-side response handling, as a pure module.
 *
 * CLIENT-SAFE: pure TypeScript + the contracts package. No Prisma, no server
 * imports — `app/(app)/referrals/page.tsx` is a client component and imports
 * this directly, and the message constant is imported by both API routes so
 * the copy cannot drift between server and client.
 *
 * Exists because `fetch(...).then(r => r.json()).then(setData)` treats EVERY
 * response as the success payload: once the routes started answering 403 for
 * an identity with no live membership, that shape rendered an undefined link
 * and undefined counters, and the Copy button reported success on `undefined`.
 */
import {
  ReferralsResponseSchema,
  type ReferralsResponse,
} from "@thesitebook/contracts";

/**
 * The single copy for "you have no internal account". Imported by
 * `app/api/referrals/route.ts`, `app/api/mobile/v1/referrals/route.ts` (so
 * both surfaces answer identically) and used as the client's fallback when a
 * 403 body is missing or unparseable.
 */
export const REFERRALS_NO_INTERNAL_ACCOUNT_MESSAGE =
  "Invite & Earn is for builder accounts — the free month is credited to a subscription.";

/** Generic client-side failure copy. */
export const REFERRALS_LOAD_ERROR_MESSAGE =
  "Couldn't load your referral link.";

/**
 * Why `POST /api/referrals/claim` refused (#2311).
 *
 * Every one of these is SETTLED: retrying the same code as the same user can
 * never turn it into a success. The claim client (`components/analytics/
 * utm-capture.tsx`) stores the code in `localStorage` and re-posts it on every
 * page load until the server accepts it, so a refusal that is not marked
 * terminal becomes an infinite retry.
 */
export const REFERRAL_CLAIM_REFUSAL_CODES = [
  /** No live user owns that code, or it is the caller's own. */
  "INVALID_CODE",
  /** The caller already has a referrer; `User.referredBy` is set-once. */
  "ALREADY_REFERRED",
  /**
   * The caller is not new to a paid plan — on one today, or churned back to
   * starter with durable prior-payment proof (`everPaidAt`). They cannot
   * "convert" through a referral they only clicked, and counting them would
   * inflate the referrer's reward — the inflation #2311 exists to remove.
   */
  "ALREADY_PAID",
] as const;
export type ReferralClaimRefusalCode =
  (typeof REFERRAL_CLAIM_REFUSAL_CODES)[number];

/** The wire body for a refused claim. Built by the route, read by the client. */
export interface ReferralClaimRefusalBody {
  error: string;
  code: ReferralClaimRefusalCode;
  /**
   * Always `true` on a refusal. The CLIENT keys off this field rather than the
   * code list, so the failure direction is right in both places: a refusal
   * code this client has never heard of still stops the retry loop, and a
   * transient 500 (which carries no `terminal`) still retries. The code is for
   * humans and tests.
   */
  terminal: true;
}

/** Human-readable copy per refusal reason — one source for route and tests. */
const REFERRAL_CLAIM_REFUSAL_MESSAGES: Record<
  ReferralClaimRefusalCode,
  string
> = {
  INVALID_CODE: "Invalid referral code",
  ALREADY_REFERRED: "This account already has a referrer",
  ALREADY_PAID:
    "Referral credit is for builders new to a paid plan — this account has already had one",
};

/** Build the refusal body. The single producer of the shape below. */
export function referralClaimRefusalBody(
  code: ReferralClaimRefusalCode,
): ReferralClaimRefusalBody {
  return { error: REFERRAL_CLAIM_REFUSAL_MESSAGES[code], code, terminal: true };
}

/**
 * Should the client STOP retrying this claim and forget the stored code?
 *
 * True for any 2xx (the code was accepted) and for any body the server marked
 * `terminal`. Everything else — a 500, a network failure, an unparseable body
 * — is treated as retryable, because dropping the stored code on a transient
 * failure would silently lose a real referral.
 *
 * Shared with `referralClaimRefusalBody` above so the producer and the consumer
 * cannot drift; `__tests__/api/referrals-claim.test.ts` pins that every code in
 * `REFERRAL_CLAIM_REFUSAL_CODES` round-trips through both.
 */
export function isSettledReferralClaim(
  ok: boolean,
  body: unknown,
): boolean {
  if (ok) return true;
  if (typeof body !== "object" || body === null) return false;
  return (body as { terminal?: unknown }).terminal === true;
}

export type ReferralsFetchResult =
  | { kind: "ok"; data: ReferralsResponse }
  | { kind: "forbidden"; message: string }
  | { kind: "error"; message: string };

/** The bit of `Response` this helper needs — keeps it trivially testable. */
export interface ReferralsHttpResponse {
  ok: boolean;
  status: number;
  json: () => Promise<unknown>;
}

function errorMessageFrom(body: unknown): string | null {
  if (typeof body !== "object" || body === null) return null;
  const { error } = body as { error?: unknown };
  return typeof error === "string" && error.length > 0 ? error : null;
}

/**
 * Turn a `/api/referrals` response into a discriminated result.
 *
 * House rule: **`res.ok` is not success.** A 200 whose body does not match the
 * wire contract is an ERROR, not data — otherwise a drifted or truncated
 * payload renders an undefined link that the user can copy and share. The JSON
 * echo must parse before this returns `ok`.
 */
export async function readReferralsResponse(
  res: ReferralsHttpResponse,
): Promise<ReferralsFetchResult> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }

  if (!res.ok) {
    const message = errorMessageFrom(body);
    if (res.status === 403) {
      return {
        kind: "forbidden",
        message: message ?? REFERRALS_NO_INTERNAL_ACCOUNT_MESSAGE,
      };
    }
    return { kind: "error", message: message ?? REFERRALS_LOAD_ERROR_MESSAGE };
  }

  const parsed = ReferralsResponseSchema.safeParse(body);
  if (!parsed.success) {
    return { kind: "error", message: REFERRALS_LOAD_ERROR_MESSAGE };
  }
  return { kind: "ok", data: parsed.data };
}
